import type { Iso8601DateTime, Uuid } from "@vesta/common-type-definitions/typeDefinitions";
import { z as zod } from "zod";
import { iso8601DateTimeZodValidator, uuidZodValidator } from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type UserDefinedObjectTemplateAdditionalColumn,
    userDefinedObjectTemplateAdditionalColumnsValidator,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {
    type UserDefinedObjectTemplate,
    UserDefinedObjectTemplateStatus,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type { WorkspaceCapability } from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type { WorkspaceCapabilityType } from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";

export type TuffexProductCatalogue = {
    additionalColumns: Array<UserDefinedObjectTemplateAdditionalColumn>;
    configuration: Record<string, unknown>;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    description: string | null;
    displayName: string;
    id: Uuid;
    instanceDisplayNameTemplate: string;
    parentId: Uuid | null;
    status: TuffexProductCatalogueStatus;
    udoTemplateId: Uuid;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    workspaceId: Uuid;
};

/** Same UUID values as ProductCatalogueTemplateStatus so migrated rows stay valid. */
export enum TuffexProductCatalogueStatus {
    active = "b335555a-0e77-43de-b19e-770adc38bc5e",
    archived = "0e3858b9-cebc-4f60-b613-ce79c79c12a1",
    deleted = "e7a6313f-657e-4fef-92b5-b09b1234fa3a",
}

export const tuffexProductCatalogueValidator: zod.ZodType<TuffexProductCatalogue> = zod.object({
    additionalColumns: userDefinedObjectTemplateAdditionalColumnsValidator,
    configuration: zod.record(zod.string(), zod.unknown()),
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    description: zod.string().nullable(),
    displayName: zod.string(),
    id: uuidZodValidator,
    instanceDisplayNameTemplate: zod.string(),
    parentId: uuidZodValidator.nullable(),
    status: zod.enum(TuffexProductCatalogueStatus),
    udoTemplateId: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    workspaceId: uuidZodValidator,
});

/** Runtime view: no separate catalogue-template persistence or public template endpoint. */
export function getTuffexProductCataloguesFromWorkspaceCapability({ allUdoTemplates, workspaceCapability }: {
    allUdoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
    workspaceCapability: WorkspaceCapability<WorkspaceCapabilityType.tuffexProductCatalogue>;
}): Array<TuffexProductCatalogue> {
    const metadata = new Map(
        allUdoTemplates.filter((template) => template.workspaceId === workspaceCapability.workspaceId).map(
            (template) => [template.id, template],
        ),
    );
    const entries = workspaceCapability.configuration.catalogueTemplates;
    const bindings = workspaceCapability.udoBindings.catalogueUdoTemplates;
    for (const key of Object.keys(bindings)) {
        if (entries[uuidZodValidator.parse(key)] === undefined) {
            throw new Error(`Catalogue binding ${key} has no business configuration`);
        }
    }
    return Object.entries(entries).map(([key, entry]) => {
        const id = uuidZodValidator.parse(key);
        const binding = bindings[id];
        const template = binding === undefined ? undefined : metadata.get(binding.udoTemplateId);
        if (template === undefined) {
            throw new Error(`Missing bound UDO metadata for Tuffex catalogue ${id}`);
        }
        const businessColumns = template.additionalColumns.filter((column) =>
            column.id !== entry.parentColumnId && column.id !== entry.statusColumnId
        );
        return {
            additionalColumns: businessColumns,
            configuration: entry.configuration,
            createdAt: template.createdAt,
            createdBy: template.createdBy,
            description: template.description,
            displayName: template.displayName,
            id,
            instanceDisplayNameTemplate: template.instanceDisplayNameTemplate,
            parentId: entry.parentId,
            status: entry.deleted
                ? TuffexProductCatalogueStatus.deleted
                : template.status === UserDefinedObjectTemplateStatus.active
                ? TuffexProductCatalogueStatus.active
                : TuffexProductCatalogueStatus.archived,
            udoTemplateId: template.id,
            updatedAt: template.updatedAt,
            updatedBy: template.updatedBy,
            workspaceId: workspaceCapability.workspaceId,
        };
    });
}
